import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Edit2, 
  Heart, 
  Smile, 
  ShoppingBag, 
  Fuel, 
  Gift,
  Search,
  ToggleLeft,
  ToggleRight,
  Users
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface BenefitPlan {
  id: string;
  name: string;
  description: string;
  category: string;
  monthlyPrice: string;
  dependentPrice: string;
  enrollmentFee: string;
  maxDependents: number;
  waitingPeriodDays: number;
  minContractMonths: number;
  requiresApproval: boolean;
  eligibleUserTypes: string[];
  subsidyType?: string;
  subsidyValue?: string;
  isActive: boolean;
  template?: {
    id: string;
    name: string;
  };
  _count?: {
    subscriptions: number;
  };
}

interface ProviderTemplate {
  id: string;
  name: string;
  category: string;
}

const categoryIcons: Record<string, React.ReactNode> = {
  HEALTH: <Heart className="w-5 h-5" />,
  DENTAL: <Smile className="w-5 h-5" />,
  SUPERMARKET: <ShoppingBag className="w-5 h-5" />,
  GAS_STATION: <Fuel className="w-5 h-5" />,
  GIFT_CARD: <Gift className="w-5 h-5" />,
};

const categoryLabels: Record<string, string> = {
  HEALTH: 'Plano de Saúde',
  DENTAL: 'Plano Odontológico',
  SUPERMARKET: 'Rede de Supermercados',
  GAS_STATION: 'Rede de Postos',
  GIFT_CARD: 'Vale Presente',
  MEAL: 'Vale Refeição',
  PHARMACY: 'Farmácia',
  EDUCATION: 'Educação',
  WELLNESS: 'Bem-estar',
  OTHER: 'Outros',
};

const categoryOptions = Object.entries(categoryLabels).map(([value, label]) => ({
  value,
  label,
}));

const userTypeLabels: Record<string, string> = {
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
};

export function BenefitPlans() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BenefitPlan | null>(null);
  const [formData, setFormData] = useState({
    templateId: '',
    name: '',
    description: '',
    category: 'HEALTH',
    monthlyPrice: '',
    dependentPrice: '',
    enrollmentFee: '',
    maxDependents: 0,
    waitingPeriodDays: 0,
    minContractMonths: 0,
    requiresApproval: true,
    eligibleUserTypes: ['EMPLOYEE', 'CUSTOMER', 'SUPPLIER'],
    subsidyType: '',
    subsidyValue: '',
  });

  const { data: plansData, isLoading, error } = useQuery<{ plans: BenefitPlan[]; total: number }>({
    queryKey: ['admin-benefit-plans', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await api.get(`${ENDPOINTS.benefits.adminPlans}?${params}`);
      return response.data;
    },
  });

  const plans = plansData?.plans || [];

  const { data: templates = [] } = useQuery<ProviderTemplate[]>({
    queryKey: ['benefit-templates'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.adminTemplates);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        monthlyPrice: Math.round(parseFloat(data.monthlyPrice || '0') * 100),
        dependentPrice: Math.round(parseFloat(data.dependentPrice || '0') * 100),
        enrollmentFee: Math.round(parseFloat(data.enrollmentFee || '0') * 100),
        subsidyValue: data.subsidyValue ? Math.round(parseFloat(data.subsidyValue) * 100) : undefined,
      };
      const response = await api.post(ENDPOINTS.benefits.adminPlanCreate, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-benefit-plans'] });
      handleCloseModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const payload = {
        ...data,
        monthlyPrice: Math.round(parseFloat(data.monthlyPrice || '0') * 100),
        dependentPrice: Math.round(parseFloat(data.dependentPrice || '0') * 100),
        enrollmentFee: Math.round(parseFloat(data.enrollmentFee || '0') * 100),
        subsidyValue: data.subsidyValue ? Math.round(parseFloat(data.subsidyValue) * 100) : undefined,
      };
      const response = await api.put(ENDPOINTS.benefits.adminPlanUpdate(id), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-benefit-plans'] });
      handleCloseModal();
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.put(ENDPOINTS.benefits.adminPlanUpdate(id), { isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-benefit-plans'] });
    },
  });

  const handleOpenModal = (plan?: BenefitPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        templateId: plan.template?.id || '',
        name: plan.name,
        description: plan.description || '',
        category: plan.category,
        monthlyPrice: (Number(plan.monthlyPrice) / 100).toString(),
        dependentPrice: (Number(plan.dependentPrice) / 100).toString(),
        enrollmentFee: (Number(plan.enrollmentFee) / 100).toString(),
        maxDependents: plan.maxDependents,
        waitingPeriodDays: plan.waitingPeriodDays,
        minContractMonths: plan.minContractMonths,
        requiresApproval: plan.requiresApproval,
        eligibleUserTypes: plan.eligibleUserTypes,
        subsidyType: plan.subsidyType || '',
        subsidyValue: plan.subsidyValue ? (Number(plan.subsidyValue) / 100).toString() : '',
      });
    } else {
      setEditingPlan(null);
      setFormData({
        templateId: '',
        name: '',
        description: '',
        category: 'HEALTH',
        monthlyPrice: '',
        dependentPrice: '',
        enrollmentFee: '',
        maxDependents: 0,
        waitingPeriodDays: 0,
        minContractMonths: 0,
        requiresApproval: true,
        eligibleUserTypes: ['EMPLOYEE', 'CUSTOMER', 'SUPPLIER'],
        subsidyType: '',
        subsidyValue: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleToggleUserType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      eligibleUserTypes: prev.eligibleUserTypes.includes(type)
        ? prev.eligibleUserTypes.filter(t => t !== type)
        : [...prev.eligibleUserTypes, type],
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Planos de Benefícios</h1>
          <p className="text-surface-400 mt-1">
            Gerencie os planos de benefícios disponíveis para seus usuários
          </p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Plano
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          placeholder="Buscar planos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />
      </div>

      {/* Plans List */}
      {error ? (
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Erro ao carregar planos
          </h3>
          <p className="text-surface-400 mb-4">
            {(error as any)?.response?.data?.message || 'Não foi possível carregar os planos de benefícios'}
          </p>
          <Button onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </Card>
      ) : isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-24 bg-surface-700 rounded-lg mb-4" />
              <div className="h-4 bg-surface-700 rounded w-3/4 mb-2" />
              <div className="h-3 bg-surface-700 rounded w-1/2" />
            </Card>
          ))}
        </div>
      ) : plans.length === 0 ? (
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Nenhum plano cadastrado
          </h3>
          <p className="text-surface-400 mb-4">
            Crie seu primeiro plano de benefícios
          </p>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Criar Plano
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className="relative">
              {/* Status Toggle */}
              <button
                onClick={() => toggleActiveMutation.mutate({ id: plan.id, isActive: !plan.isActive })}
                className="absolute top-4 right-4"
                title={plan.isActive ? 'Desativar' : 'Ativar'}
              >
                {plan.isActive ? (
                  <ToggleRight className="w-6 h-6 text-green-400" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-surface-500" />
                )}
              </button>

              {/* Category */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-primary-400">
                  {categoryIcons[plan.category] || <Gift className="w-5 h-5" />}
                </span>
                <span className="text-sm text-surface-400">
                  {categoryLabels[plan.category] || plan.category}
                </span>
              </div>

              {/* Name & Description */}
              <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
              <p className="text-surface-400 text-sm mb-4 line-clamp-2">
                {plan.description || 'Sem descrição'}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-2xl font-bold text-primary-400">
                  {formatCurrency(Number(plan.monthlyPrice) / 100)}
                </span>
                <span className="text-surface-400 text-sm">/mês</span>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-surface-400 mb-4">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {plan._count?.subscriptions || 0} adesões
                </span>
                {plan.maxDependents > 0 && (
                  <span>Até {plan.maxDependents} dep.</span>
                )}
              </div>

              {/* Eligible Types */}
              <div className="flex flex-wrap gap-1 mb-4">
                {plan.eligibleUserTypes.map((type) => (
                  <Badge key={type} variant="info" size="sm">
                    {userTypeLabels[type] || type}
                  </Badge>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-surface-700">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleOpenModal(plan)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Editar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPlan ? 'Editar Plano' : 'Novo Plano de Benefício'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Template Selection */}
          {!editingPlan && (
            <Select
              label="Template de Provider"
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
              options={[
                { value: '', label: 'Selecione um template (opcional)' },
                ...templates.map(t => ({ value: t.id, label: `${t.name} (${t.category})` })),
              ]}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nome do Plano"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Select
              label="Categoria"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              options={categoryOptions}
            />
          </div>

          <Input
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Preço Mensal (R$)"
              type="number"
              step="0.01"
              value={formData.monthlyPrice}
              onChange={(e) => setFormData({ ...formData, monthlyPrice: e.target.value })}
              required
            />
            <Input
              label="Preço Dependente (R$)"
              type="number"
              step="0.01"
              value={formData.dependentPrice}
              onChange={(e) => setFormData({ ...formData, dependentPrice: e.target.value })}
            />
            <Input
              label="Taxa de Adesão (R$)"
              type="number"
              step="0.01"
              value={formData.enrollmentFee}
              onChange={(e) => setFormData({ ...formData, enrollmentFee: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Máx. Dependentes"
              type="number"
              value={formData.maxDependents}
              onChange={(e) => setFormData({ ...formData, maxDependents: parseInt(e.target.value) || 0 })}
            />
            <Input
              label="Carência (dias)"
              type="number"
              value={formData.waitingPeriodDays}
              onChange={(e) => setFormData({ ...formData, waitingPeriodDays: parseInt(e.target.value) || 0 })}
            />
            <Input
              label="Contrato Mín. (meses)"
              type="number"
              value={formData.minContractMonths}
              onChange={(e) => setFormData({ ...formData, minContractMonths: parseInt(e.target.value) || 0 })}
            />
          </div>

          {/* Subsidy */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Tipo de Subsídio"
              value={formData.subsidyType}
              onChange={(e) => setFormData({ ...formData, subsidyType: e.target.value })}
              options={[
                { value: '', label: 'Sem subsídio' },
                { value: 'PERCENTAGE', label: 'Percentual' },
                { value: 'FIXED', label: 'Valor Fixo' },
              ]}
            />
            {formData.subsidyType && (
              <Input
                label={formData.subsidyType === 'PERCENTAGE' ? 'Percentual (%)' : 'Valor (R$)'}
                type="number"
                step={formData.subsidyType === 'PERCENTAGE' ? '1' : '0.01'}
                value={formData.subsidyValue}
                onChange={(e) => setFormData({ ...formData, subsidyValue: e.target.value })}
              />
            )}
          </div>

          {/* Eligible User Types */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Tipos de Usuário Elegíveis
            </label>
            <div className="flex gap-2">
              {Object.entries(userTypeLabels).map(([type, label]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleToggleUserType(type)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm transition-colors
                    ${formData.eligibleUserTypes.includes(type)
                      ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500'
                      : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                    }
                  `}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Requires Approval */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requiresApproval}
              onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
              className="w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-surface-300">Requer aprovação do administrador</span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {editingPlan ? 'Salvar' : 'Criar Plano'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default BenefitPlans;
